/* Luxe Organix — campaign banner controller (sections/lux-banners.liquid).

   The track is a native scroll-snap strip and works with no JS. This adds:
     • tab sync — the tab for whichever slide is in view is current;
     • autoplay — driven by the current tab's CSS fill animation: when it ends,
       advance. Hover, focus, the pause button and "off-screen" all pause that
       animation, so they pause the rotation with no second timer to drift;
     • editor support — selecting a block shows its slide, nothing autoplays.

   WCAG 2.2.2: never starts under prefers-reduced-motion; pausable. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const design = !!(window.Shopify && window.Shopify.designMode);

  function init(root) {
    if (!root || root.dataset.luxBnInit) return;
    root.dataset.luxBnInit = '1';

    const track = root.querySelector('[data-lux-bn-track]');
    const slides = [...root.querySelectorAll('[data-lux-bn-slide]')];
    const tabs = [...root.querySelectorAll('[data-lux-bn-tab]')];
    const pauseBtn = root.querySelector('[data-lux-bn-pause]');
    if (!track || slides.length < 2) return;

    let cur = 0;
    let steering = false; // true while a programmatic scroll is in flight
    let steerTimer = 0;
    let userPaused = false;

    const canPlay = () => !reduce.matches && !design;

    function mark(i) {
      cur = i;
      tabs.forEach((t, k) => {
        t.classList.toggle('is-current', k === i);
        t.classList.toggle('is-seen', k < i);
        if (k === i) t.setAttribute('aria-current', 'true');
        else t.removeAttribute('aria-current');
      });
      // Restart the current fill so each slide gets its full interval.
      const fill = tabs[i] && tabs[i].querySelector('.lux-bn__fill');
      if (fill) {
        fill.style.animation = 'none';
        void fill.offsetWidth;
        fill.style.animation = '';
      }
    }

    function go(i, smooth = true) {
      i = (i + slides.length) % slides.length;
      steering = true;
      clearTimeout(steerTimer);
      track.scrollTo({
        left: i * track.clientWidth,
        behavior: smooth && !reduce.matches ? 'smooth' : 'auto',
      });
      mark(i);
      // scrollend is not universal (Safari); a timeout is the fallback.
      steerTimer = setTimeout(() => { steering = false; }, 900);
    }

    track.addEventListener('scrollend', () => {
      clearTimeout(steerTimer);
      steering = false;
    });

    // Which slide is in view — covers swipes, trackpads and keyboard scrolls.
    const seen = new IntersectionObserver((entries) => {
      if (steering) return;
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio >= 0.6) {
          const i = slides.indexOf(e.target);
          if (i !== cur) mark(i);
        }
      });
    }, { root: track, threshold: [0.6] });
    slides.forEach((s) => seen.observe(s));

    tabs.forEach((t, k) => t.addEventListener('click', () => go(k)));

    // Autoplay: advance when the current fill finishes.
    root.addEventListener('animationend', (e) => {
      if (e.animationName !== 'lux-bn-fill') return;
      if (!root.classList.contains('is-playing') || userPaused) return;
      go(cur + 1);
    });

    function syncPlay() {
      const play = canPlay();
      root.classList.toggle('is-playing', play);
      if (pauseBtn) pauseBtn.hidden = !play;
    }
    syncPlay();
    if (reduce.addEventListener) reduce.addEventListener('change', syncPlay);

    if (pauseBtn) {
      pauseBtn.addEventListener('click', () => {
        userPaused = !userPaused;
        root.classList.toggle('is-paused', userPaused);
        pauseBtn.setAttribute('aria-label', userPaused ? 'Play banners' : 'Pause banners');
      });
    }

    // Hold while off-screen or in a hidden tab.
    let offscreen = false;
    const hold = () => root.classList.toggle('is-hold', offscreen || document.hidden);
    new IntersectionObserver((entries) => {
      offscreen = !entries[0].isIntersecting;
      hold();
    }, { threshold: 0.25 }).observe(root);
    document.addEventListener('visibilitychange', hold);

    // Slides after the first are lazy. A horizontal scroller can reach one
    // before the browser decides it is "near", so fetch them once the page
    // has loaded rather than mid-transition.
    const warm = () => root.querySelectorAll('img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    if (document.readyState === 'complete') warm();
    else window.addEventListener('load', warm, { once: true });

    // Theme editor: show the selected block's slide.
    root._luxBnGo = (el) => {
      const i = slides.indexOf(el.closest('[data-lux-bn-slide]'));
      if (i > -1) go(i, false);
    };
  }

  const boot = (scope) => scope.querySelectorAll('[data-lux-bn]').forEach(init);
  boot(document);

  if (design) {
    document.addEventListener('shopify:section:load', (e) => boot(e.target));
    document.addEventListener('shopify:block:select', (e) => {
      const root = e.target.closest && e.target.closest('[data-lux-bn]');
      if (root && root._luxBnGo) root._luxBnGo(e.target);
    });
  }
})();
